import { useEffect } from 'react';

import { Icon } from '@/components/ui/Icon';
import { sound } from '@/game/audio/sound';
import { useRun } from '@/store/run';

/**
 * Searching the wall beside you — and the card that says what was behind it.
 *
 * `POST /cave/search` has existed since the run engine was written and had **no
 * caller anywhere in the app**: `api/endpoints.ts` typed it, nothing invoked it, and
 * the only route in the game to a Hidden Chest, a Mystery Door or a layer of the map
 * to the next cave was unreachable. This is the missing half.
 *
 * ## Why the controls are on the walls and not in the button bar
 *
 * The bar already holds five controls at the bottom of a 390px-wide phone, and two
 * more would take the tap targets under the size a thumb can hit. But the better
 * reason is that the question is *spatial*: "search the wall to your left" has an
 * obvious place to be asked, and it is the left wall. Putting it there means the
 * player never has to map a button onto a direction.
 *
 * ## They are drawn with light
 *
 * The cave palette is near-black and the wall behind these is the darkest surface in
 * frame, so a tab drawn as a shadow — a dark panel with a dark border — is invisible
 * at the exact moment it needs to be found. Both tabs carry a warm rim and a torch-lit
 * glyph, and go dim rather than dark when they cannot be used.
 */
export function WallSearch() {
  const search = useRun((s) => s.search);
  const searching = useRun((s) => s.searching);
  const phase = useRun((s) => s.phase);
  const torchFuel = useRun((s) => s.torchFuel);

  /*
   * A deny-list, not an allow-list, and the difference is the whole mechanic.
   *
   * The first pass allowed `walking` and `blocked` only — which reads sensibly and is
   * wrong, because a breakable rock in the path puts the run in `breaking` and that is
   * where a descent spends most of its time. On screen both tabs sat greyed out
   * through every rock, so the mechanic looked broken far more often than it looked
   * available. The server does not care whether a rock is in the way, and neither
   * should this: what must be excluded is only the moments where the run is mid-
   * transaction and a second request would race it.
   *
   * Fuel is checked because the server refuses below `FUEL_PER_SEARCH`, and spending
   * a tap to be told no is worse than a tab that says no before the tap.
   */
  const busyPhase =
    phase === 'idle' || phase === 'loot' || phase === 'adPending' || phase === 'stepping';
  const canSearch = !searching && !busyPhase && torchFuel > 1.5;

  return (
    <>
      <Tab side="left" disabled={!canSearch} busy={searching} onSearch={() => void search('left')} />
      <Tab side="right" disabled={!canSearch} busy={searching} onSearch={() => void search('right')} />
    </>
  );
}

function Tab({ side, disabled, busy, onSearch }: {
  side: 'left' | 'right'; disabled: boolean; busy: boolean; onSearch: () => void;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onSearch}
      disabled={disabled}
      aria-label={`Search the ${side} wall`}
      className={`absolute top-1/2 z-[9] flex -translate-y-1/2 flex-col items-center gap-1
                  transition-opacity disabled:opacity-30
                  ${side === 'left' ? 'left-1.5' : 'right-1.5'}`}
      style={{ touchAction: 'manipulation' }}
    >
      {/*
        A lit ring, not a dark panel.

        The first pass was a shadow-coloured tab against the darkest wall in frame,
        and in a screenshot it was invisible — the same failure this cave has produced
        three times now. Drawn with light instead: a warm rim, a warm glyph, and a
        glow that separates it from the rock behind it.
      */}
      <span
        className="grid h-9 w-9 place-items-center rounded-full border
                   border-[rgba(255,196,120,.68)] text-torch-core backdrop-blur-[2px]"
        style={{
          background:
            'radial-gradient(circle at 50% 38%, rgba(255,186,102,.30), rgba(26,17,10,.92) 72%)',
          boxShadow: '0 0 16px -4px rgba(255,178,94,.75), inset 0 0 8px -3px rgba(255,214,160,.55)',
        }}
        aria-hidden
      >
        <Icon name={busy ? 'hourglass' : 'radar'} size={17} />
      </span>
      {/* Horizontal. The pixel font has no upright vertical form, so `writingMode`
          laid SEARCH on its side and it read as decoration rather than a label. */}
      <span className="rounded bg-black/60 px-1 font-pixel text-[5px] tracking-wide text-torch-warm">
        SEARCH
      </span>
    </button>
  );
}

/**
 * What the wall gave up.
 *
 * Rendered over the corridor rather than as a modal with a scrim: a search does not
 * interrupt the run — the player can walk on with the card still up — and dimming
 * the whole cave for a line of text would make a 9% find feel like an event that
 * stops the game.
 *
 * A `none` result is shown too. The player spent fuel asking a question; answering
 * with nothing on screen is indistinguishable from a broken button, which is how a
 * mechanic gets abandoned.
 */
export function WallSearchCard() {
  const event = useRun((s) => s.searchEvent);
  const dismiss = useRun((s) => s.dismissSearchEvent);

  /* Auto-dismiss, because this is a passing beat and a card that needs closing turns
     every search into two taps. Longer for a find than for a miss — "nothing here"
     has been read by the time it lands. */
  const holdMs = event == null ? 0 : event.type === 'none' ? 2600 : 5000;
  useEffect(() => {
    if (holdMs === 0) return;
    const t = window.setTimeout(dismiss, holdMs);
    return () => window.clearTimeout(t);
  }, [holdMs, dismiss, event]);

  /* One sound per result, and only for the ones that ARE a result. A chime for
     "solid rock, nothing here" trains the player to stop hearing the chime.
     `portal` for a finished map because it is the one cue in the set that means
     somewhere new opened, which is exactly what just happened. */
  useEffect(() => {
    if (!event || event.type === 'none') return;
    sound.playSfx(event.opened ? 'portal' : 'crystal');
  }, [event]);

  if (!event) return null;

  const opened = event.type === 'map_fragment' && event.opened === true;
  const accent =
    event.type === 'none' ? 'rgba(255,186,102,.24)'
    : opened ? 'var(--coin-gold)'
    : 'var(--torch-warm)';

  return (
    <div
      className="pointer-events-none absolute inset-x-3 bottom-3 z-[12]"
      role="status"
      aria-live="polite"
    >
      <div
        className="rounded-lg border bg-[linear-gradient(180deg,rgba(32,20,16,.95),rgba(12,8,5,.96))]
                   px-3 py-2.5 backdrop-blur-sm"
        style={{ borderColor: accent, boxShadow: `0 0 24px -8px ${accent}` }}
      >
        <div className="flex items-start gap-2.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-black/45"
                style={{ color: accent }} aria-hidden>
            <Icon
              name={
                event.type === 'map_fragment' ? (opened ? 'map' : 'mapPiece')
                : event.type === 'hidden_chest' ? 'gift'
                : event.type === 'mystery_door' ? 'key'
                : 'radar'
              }
              size={17}
            />
          </span>

          <div className="min-w-0 flex-1">
            {event.name && (
              <p className="font-pixel text-[7px] tracking-wide" style={{ color: accent }}>
                {event.name.toUpperCase()}
              </p>
            )}
            <p className="mt-0.5 text-[11.5px] leading-snug text-ink-hi">{event.message}</p>

            {/* Progress toward the cave this layer belongs to. Shown on the card
                rather than only on the map screen, because the player is 300 m
                underground and the map screen is four taps away. */}
            {event.type === 'map_fragment' && event.required != null && (
              <div className="mt-1.5">
                <div className="flex items-center justify-between text-[9.5px]">
                  <span className="text-ink-dim">{event.cave_name ?? 'Map'}</span>
                  <b className="tabular text-ink-hi">{event.fragments} / {event.required}</b>
                </div>
                <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white/12">
                  <span
                    className="block h-full rounded-full transition-[width] duration-500"
                    style={{
                      width: `${Math.min(100, ((event.fragments ?? 0) / event.required) * 100)}%`,
                      background: `linear-gradient(90deg,${accent},#FFF3DC)`,
                    }}
                  />
                </span>
              </div>
            )}

            {event.coins != null && event.coins > 0 && (
              <p className="mt-1 flex items-center gap-1 text-[11px] font-bold text-coin tabular">
                <Icon name="coin" size={12} />
                <span className="sr-only">Shwe Coins</span>
                +{event.coins.toLocaleString('en-US')}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
